import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import { notification, Spin, Empty, DatePicker } from "antd";
import moment from "moment";
import {
  HiOutlineCurrencyDollar,
  HiOutlineTrendingUp,
  HiOutlineCreditCard,
  HiOutlineLibrary,
  HiOutlineDownload,
  HiOutlinePrinter,
  HiOutlineChartBar,
  HiOutlineSparkles
} from 'react-icons/hi';
import DashboardChart from "../Dashboard/DashboardChart";

const { RangePicker } = DatePicker;

const RevenueMaster = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [revenueData, setRevenueData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [dateRange, setDateRange] = useState(() => [moment().startOf('month'), moment().endOf('month')]);

  const handleDownload = () => {
    if (!revenueData) return;
    const currency = "₹";
    const rows = [
      ["Financial Overview Report"],
      ["Metric", "Amount"],
      ["Total Revenue", `${currency} ${Number(revenueData.revenueAmount || 0).toFixed(2)}`],
      ["Online Paid", `${currency} ${Number(revenueData.onlineAmount || 0).toFixed(2)}`],
      ["Clinic Paid", `${currency} ${Number(revenueData.paidClinicAmount || 0).toFixed(2)}`],
      ["Net Income", `${currency} ${Number(revenueData.doctorIncome || 0).toFixed(2)}`],
      [],
      ["Appointment-wise Breakdown"],
      ["S.No", "Patient Name", "Date", "Service", "Payment Mode", `Amount (${currency})`],
    ];
    transactions.forEach((t, i) => {
      rows.push([
        i + 1,
        t.patientName || t.patient_name || "-",
        t.booking_date || "-",
        t.service_name || "-",
        t.payment_mode || "-",
        Number(t.paid_amount || 0).toFixed(2)
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, "'")}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "appointment_revenue_report.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    fetchRevenueData();
  }, [dateRange]);

  const fetchRevenueData = async () => {
    setLoading(true);
    try {
      const doctorId = Cookies.get("Doctor_id") || Cookies.get("doctor_id") || localStorage.getItem("doctor_id") || localStorage.getItem("Doctor_id") || 'all';
      const clinicId = Cookies.get("Clinic_id") || Cookies.get("clinic_id") || localStorage.getItem("clinic_id") || localStorage.getItem("Clinic_id");
      const from_date = dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : moment().startOf('month').format("YYYY-MM-DD");
      const to_date = dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : moment().endOf('month').format("YYYY-MM-DD");

      const response = await axiosInstance.post("doctor/getRevenueReport", {
        doctor_id: doctorId,
        clinic_id: clinicId,
        from_date,
        to_date
      });

      if (response.data.status === 1 && response.data.data) {
        const data = response.data.data;
        const summary = data.summary || {};
        const txns = Array.isArray(data.transactions) ? data.transactions : [];

        setRevenueData({
          revenueAmount: Number(summary.total_revenue || 0),
          onlineAmount: Number(summary.online_paid || 0),
          paidClinicAmount: Number(summary.clinic_paid || 0),
          doctorIncome: Number(summary.net_income || 0),
          paidInsuranceAmount: Number(summary.insurance_paid || 0),
          tomShareAmount: Number(summary.avelator_share || 0),
          incentiveAmount: Number(summary.referral_incentive || 0),
          insuranceDeducAmount: Number(summary.insurance_deductions || 0),
          billingModel: summary.billing_model || 'subscription'
        });

        const formattedTxns = txns.map((item) => ({
          patientName: item.patientName || item.patient_name || 'Patient',
          doctorName: item.doctorName || item.doctor_name || '',
          booking_date: item.book_date,
          service_name: item.service_name || 'Consultation',
          payment_mode: (item.payment_status == 1 && item.payment_gateway && item.payment_gateway.toLowerCase() !== 'cash') ? item.payment_gateway : 'Clinic Cash',
          paid_amount: Number(item.book_amount || item.doc_booking_amount || 0)
        }));

        setTransactions(formattedTxns);

        // Prepare chart data grouped across the selected date range
        const startDate = dateRange && dateRange[0] ? dateRange[0] : moment().startOf('month');
        const endDate = dateRange && dateRange[1] ? dateRange[1] : moment().endOf('month');
        const daysDiff = endDate.diff(startDate, 'day');

        const chartMap = {};
        formattedTxns.forEach((t) => {
          if (!t.booking_date) return;
          const key = daysDiff > 90
            ? moment(t.booking_date).format("MMM YYYY")
            : moment(t.booking_date).format("DD MMM");
          chartMap[key] = (chartMap[key] || 0) + t.paid_amount;
        });

        const timelinePoints = [];
        if (daysDiff > 90) {
          let curr = startDate.startOf('month');
          const lastMonth = endDate.endOf('month');
          while (curr.isBefore(lastMonth) || curr.isSame(lastMonth, 'month')) {
            const key = curr.format("MMM YYYY");
            timelinePoints.push({ month: key, value: chartMap[key] || 0 });
            curr = curr.add(1, 'month');
          }
        } else {
          let curr = startDate;
          while (curr.isBefore(endDate) || curr.isSame(endDate, 'day')) {
            const key = curr.format("DD MMM");
            timelinePoints.push({ month: key, value: chartMap[key] || 0 });
            curr = curr.add(1, 'day');
          }
        }

        setChartData(timelinePoints);
      } else {
        setRevenueData(null);
        setTransactions([]);
        setChartData([]);
      }
    } catch (err) {
      console.error("Failed to fetch revenue data", err);
      notification.error({ message: "Failed to load financial data" });
    } finally {
      setLoading(false);
    }
  };

  const totalRev = revenueData?.revenueAmount || 0;
  const onlineRev = revenueData?.onlineAmount || 0;
  const clinicRev = revenueData?.paidClinicAmount || 0;

  const onlinePct = totalRev > 0 ? Math.round((onlineRev / totalRev) * 100) : 0;
  const clinicPct = totalRev > 0 ? Math.round((clinicRev / totalRev) * 100) : 0;

  const stats = [
    { label: 'Total Revenue', value: totalRev, badge: '100%', icon: HiOutlineCurrencyDollar, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100' },
    { label: 'Online Paid', value: onlineRev, badge: `${onlinePct}%`, icon: HiOutlineCreditCard, color: 'text-teal-600', bg: 'bg-teal-50 border-teal-100' },
    { label: 'Clinic Cash', value: clinicRev, badge: `${clinicPct}%`, icon: HiOutlineLibrary, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-100' },
    { label: 'Net Income', value: revenueData?.doctorIncome || 0, badge: 'Net', icon: HiOutlineTrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
  ];

  return (
    <div className="space-y-3 animate-in fade-in duration-300 w-full text-gray-800">
      {/* Integrated Sleek Header */}
      <div className="bg-white px-3.5 py-2.5 rounded-lg border border-gray-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2.5 text-left w-full">
        <div className="text-left shrink-0">
          <h2 className="text-sm font-bold text-gray-800 leading-tight flex items-center gap-1.5 text-left m-0">
            Revenue Overview <HiOutlineSparkles className="text-amber-500" size={15} />
          </h2>
          <p className="text-gray-400 text-[11px] text-left m-0 mt-0.5">Monitor earnings, payment breakdowns, and settlement history</p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 shrink-0 w-full sm:w-auto">
          <RangePicker
            value={dateRange}
            defaultValue={[moment().startOf('month'), moment().endOf('month')]}
            onChange={(dates) => setDateRange(dates || [moment().startOf('month'), moment().endOf('month')])}
            allowClear={false}
            size="small"
            className="rounded border-gray-200 text-xs h-8 flex-1 sm:flex-none"
          />
          <button onClick={handleDownload} title="Export CSV" className="p-1.5 bg-gray-50 text-gray-500 rounded hover:bg-teal-50 hover:text-teal-600 transition-all border border-gray-200 cursor-pointer">
            <HiOutlineDownload size={15} />
          </button>
          <button onClick={handlePrint} title="Print Financial Statement" className="p-1.5 bg-gray-50 text-gray-500 rounded hover:bg-blue-50 hover:text-blue-600 transition-all border border-gray-200 cursor-pointer">
            <HiOutlinePrinter size={15} />
          </button>
        </div>
      </div>

      {/* Financial Metric Cards */}
      <div className="w-full pb-1">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((s, i) => (
            <div key={i} className="bg-white px-3.5 py-2.5 rounded-lg border border-gray-200 shadow-2xs hover:border-teal-200 transition-all group">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">{s.label}</span>
                <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-gray-100 text-gray-600">{s.badge}</span>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-base font-bold text-gray-800 leading-tight">₹ {s.value.toLocaleString()}</p>
                <div className={`${s.bg} ${s.color} p-1.5 rounded-md border shrink-0`}>
                  <s.icon size={15} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Analytics Chart & Breakdown Grid (Growth Chart Left, Payment Split Right) */}
      <div className="grid grid-cols-12 gap-3 items-stretch w-full">
        {/* Visual Revenue Trend Chart (Left side) */}
        <div className="col-span-12 md:col-span-8 bg-white p-3.5 rounded-lg border border-gray-200 shadow-2xs flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-bold text-gray-800 text-xs">Revenue Growth & Trend</h3>
              <p className="text-[10px] text-gray-400">Daily earnings distribution for selected range</p>
            </div>
            <div className="flex items-center gap-3 text-[10px] font-bold">
              <span className="flex items-center gap-1 text-teal-700">
                <span className="w-2 h-2 rounded-full bg-teal-600 inline-block" /> Revenue (₹)
              </span>
            </div>
          </div>
          <div className="h-52 w-full">
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <Spin size="small" />
              </div>
            ) : (
              <DashboardChart data={chartData} />
            )}
          </div>
        </div>

        {/* Payment Distribution & Income Breakdown (Right side) */}
        <div className="col-span-12 md:col-span-4 bg-white p-3.5 rounded-lg border border-gray-200 shadow-2xs flex flex-col justify-between space-y-3">
          <h3 className="font-bold text-gray-800 text-xs flex items-center gap-1.5 border-b border-gray-100 pb-2">
            <HiOutlineChartBar className="text-teal-600" size={15} />
            Payment Split & Net Income
          </h3>

          <div className="space-y-2.5">
            {/* Online vs Cash Progress Bar */}
            <div>
              <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 mb-1">
                <span>Online ({onlinePct}%)</span>
                <span>Clinic Cash ({clinicPct}%)</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2 flex overflow-hidden">
                <div className="bg-teal-500 h-full transition-all" style={{ width: `${onlinePct}%` }} />
                <div className="bg-purple-500 h-full transition-all" style={{ width: `${clinicPct}%` }} />
              </div>
            </div>

            <div className="space-y-1.5 pt-1">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100 text-xs">
                <span className="text-gray-600 text-[11px] font-semibold">Gross Revenue</span>
                <span className="font-bold text-gray-800">₹ {totalRev.toLocaleString()}</span>
              </div>

              {revenueData?.billingModel === 'commission' && (
                <div className="flex items-center justify-between p-2 bg-rose-50/60 rounded border border-rose-100/50 text-xs">
                  <span className="text-rose-700 text-[11px] font-semibold">Platform Fee</span>
                  <span className="font-bold text-rose-600">- ₹ {(revenueData?.tomShareAmount || 0).toLocaleString()}</span>
                </div>
              )}

              <div className="flex items-center justify-between p-2 bg-emerald-50/80 rounded border border-emerald-100 text-xs">
                <span className="text-emerald-800 text-[11px] font-bold">Net Earnings</span>
                <span className="font-bold text-emerald-700 text-sm">₹ {(revenueData?.doctorIncome || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="text-[10px] text-gray-400 font-medium italic pt-1 border-t border-gray-100">
            Billing Plan: <span className="font-bold text-gray-700 uppercase">{revenueData?.billingModel || 'Subscription'}</span>
          </div>
        </div>
      </div>

      {/* Transaction Details Table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-2xs overflow-hidden">
        <div className="px-3.5 py-2.5 border-b border-gray-100 flex items-center justify-between bg-white">
          <h3 className="font-bold text-gray-800 text-xs">Transaction Records</h3>
          <span className="text-[11px] font-semibold text-gray-400">{transactions.length} record(s)</span>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-8 text-center flex flex-col items-center justify-center gap-1.5">
              <Spin size="small" />
              <span className="text-xs text-gray-400 font-medium">Fetching transaction details...</span>
            </div>
          ) : transactions.length > 0 ? (
            <table className="w-full min-w-[600px] text-left">
              <thead className="bg-gray-50 text-gray-500 text-[9px] uppercase font-bold tracking-wider border-b border-gray-100">
                <tr>
                  <th className="px-3 py-2">Patient Details</th>
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2">Payment Mode</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {transactions.map((t, i) => (
                  <tr key={i} className="hover:bg-teal-50/20 transition-colors">
                    <td className="px-3 py-2">
                      <p className="font-bold text-gray-800 text-[11px]">{t.patientName}</p>
                      <p className="text-[9px] text-gray-400 font-semibold">{moment(t.booking_date).format('DD MMM, YYYY')}</p>
                    </td>
                    <td className="px-3 py-2 text-[11px] font-medium text-gray-600">{t.service_name}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-tight border ${
                        t.payment_mode?.toLowerCase().includes('cash')
                          ? 'bg-purple-50 text-purple-700 border-purple-100'
                          : 'bg-teal-50 text-teal-700 border-teal-100'
                      }`}>
                        {t.payment_mode || 'Clinic Cash'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-[11px] text-gray-800">₹ {Number(t.paid_amount || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-bold text-xs border-t border-gray-200">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-gray-800 text-[11px]">TOTAL REVENUE</td>
                  <td className="px-3 py-2 text-right text-teal-700 font-bold text-xs">₹ {transactions.reduce((acc, curr) => acc + (curr.paid_amount || 0), 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="py-8 text-center">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span className="text-xs font-medium text-gray-400">No transactions found for the selected date range</span>}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueMaster;